import { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLoaderData } from 'react-router-dom';
import { useImmerReducer } from 'use-immer';
import { toast } from 'react-toastify';
import { Line } from 'react-chartjs-2';
import {
    CategoryScale,
    Chart as ChartJS,
    Filler,
    Legend,
    LineElement,
    LinearScale,
    PointElement,
    Tooltip,
} from 'chart.js';
import { GlobalContext } from '../../../library/Core/GlobalContext';
import { SmartContext } from '../../../library/Core/SmartContext';
import smartReducer from '../../../library/Core/SmartReducer';
import { DispatchEvent, State } from '../../../library/Core/SmartTypes';
import DebugBarControl from '../../../library/SimpleControls/DebugBarControl';
import LoadingSpinner from '../../../library/SimpleControls/LoadingSpinner';
import { addUserSpecificDomains } from '../../../library/Service/domainService';
import AddServiceProviderModal from '../../admin/business-directory/AddServiceProviderModal';
import AlumniDetailsModal from '../../admin/business-directory/AlumniDetailsModal';
import InquiryDetailModal from '../inquiry-management/components/InquiryDetailModal';
import ServiceProviderDashboardService from './ServiceProviderDashboardService';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

const TIME_RANGE_OPTIONS = [
    { code: 'THIS_WEEK', label: 'Week' },
    { code: 'THIS_MONTH', label: 'Month' },
    { code: 'THIS_QUARTER', label: 'Quarter' },
    { code: 'THIS_YEAR', label: 'Year' },
];

const numberFormatter = new Intl.NumberFormat('en-IN');
const compactNumberFormatter = new Intl.NumberFormat('en-IN', { notation: 'compact', maximumFractionDigits: 1 });

const formatCount = (value: number) => numberFormatter.format(Number(value || 0));
const formatCompactCurrency = (value: number) => `₹${compactNumberFormatter.format(Number(value || 0))}`;
const formatDateTime = (value?: string | Date) => {
    if (!value) return '-';
    const date = value instanceof Date ? value : new Date(value);
    return new Intl.DateTimeFormat('en-IN', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
};

const formatRelativeDate = (value?: string) => {
    if (!value) return '-';
    const date = new Date(value);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const target = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    const diffDays = Math.round((today - target) / 86400000);

    if (diffDays === 0) {
        return `Today, ${new Intl.DateTimeFormat('en-IN', { hour: '2-digit', minute: '2-digit' }).format(date)}`;
    }

    if (diffDays === 1) {
        return `Yesterday, ${new Intl.DateTimeFormat('en-IN', { hour: '2-digit', minute: '2-digit' }).format(date)}`;
    }

    return new Intl.DateTimeFormat('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
};

const buildChartConfig = (chartData: any[]) => ({
    data: {
        labels: chartData.map((item) => item.label),
        datasets: [
            {
                label: 'Listing Views',
                data: chartData.map((item) => item.value),
                borderColor: '#0c4a6e',
                backgroundColor: 'rgba(12, 74, 110, 0.12)',
                fill: true,
                tension: 0.35,
                pointRadius: 4,
                pointHoverRadius: 5,
            },
        ],
    },
    options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
        },
        scales: {
            x: {
                grid: { display: false },
            },
            y: {
                beginAtZero: true,
                ticks: {
                    precision: 0,
                },
            },
        },
    },
});

const getTimeRangeDescription = (timeRange: string) => {
    switch (timeRange) {
        case 'THIS_WEEK':
            return 'this week';
        case 'THIS_QUARTER':
            return 'this quarter';
        case 'THIS_YEAR':
            return 'this year';
        default:
            return 'this month';
    }
};

const getListingStatusMeta = (status?: string, hasListing?: boolean) => {
    if (!hasListing) {
        return {
            code: 'NONE',
            badgeClass: 'bg-warning-subtle text-warning-emphasis',
            dotColor: '#d97706',
            label: 'No listing yet',
        };
    }

    switch ((status || '').toUpperCase()) {
        case 'ACTIVE':
            return {
                code: 'ACTIVE',
                badgeClass: 'bg-success-subtle text-success',
                dotColor: '#16a34a',
                label: 'Your listing is Live',
            };
        case 'INACTIVE':
            return {
                code: 'INACTIVE',
                badgeClass: 'bg-secondary-subtle text-secondary',
                dotColor: '#6b7280',
                label: 'Your listing is Inactive',
            };
        default:
            return {
                code: 'PAUSED',
                badgeClass: 'bg-secondary-subtle text-secondary',
                dotColor: '#6b7280',
                label: 'Your listing is Paused',
            };
    }
};

const ServiceProviderDashboard = () => {
    const { globalState } = useContext(GlobalContext);
    const [selectedInquiry, setSelectedInquiry] = useState<any>(null);
    const [editData, setEditData] = useState<any>(null);
    const [isActionOpen, setIsActionOpen] = useState(false);

    const [state, dispatch] = useImmerReducer<State, DispatchEvent>(smartReducer, {
        flags: { isDataLoading: true, showFormErrors: 0 },
        formValidationErrors: {},
        actions: {},
    } as any);

    const { formConfig, data, internal, routeInfo } = useLoaderData() as State;
    const domain = useMemo(() => addUserSpecificDomains(new Map([...globalState.domain]), data), [globalState.domain, data]);

    useEffect(() => {
        dispatch({
            type: 'DATA_INIT',
            payload: { formConfig, data, domain, internal, routeInfo },
        });
    }, []);

    const provider = state.data?.provider;
    const metrics = state.data?.metrics || ServiceProviderDashboardService.getEmptyMetrics();
    const funnel = state.data?.funnel || ServiceProviderDashboardService.getEmptyFunnel();
    const recentInquiries = state.data?.recentInquiries || [];
    const timeRange = state.data?.timeRange || 'THIS_MONTH';
    const hasListing = !!state.data?.hasListing;
    const chartConfig = buildChartConfig(state.data?.chartData || []);

    const reloadDashboard = async (nextTimeRange: string) => {
        dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'isLoading', value: true, errorMessages: [] } });
        try {
            const dashboardData = await ServiceProviderDashboardService.getDashboard(nextTimeRange);
            dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'provider', value: dashboardData?.provider || null, errorMessages: [] } });
            dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'metrics', value: dashboardData?.metrics || ServiceProviderDashboardService.getEmptyMetrics(), errorMessages: [] } });
            dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'chartData', value: dashboardData?.chartData || [], errorMessages: [] } });
            dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'funnel', value: dashboardData?.funnel || ServiceProviderDashboardService.getEmptyFunnel(), errorMessages: [] } });
            dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'recentInquiries', value: dashboardData?.recentInquiries || [], errorMessages: [] } });
            dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'hasListing', value: !!dashboardData?.hasListing, errorMessages: [] } });
        } catch (error) {
            console.error('Service provider dashboard load failed:', error);
            toast.error('Failed to load dashboard data');
        } finally {
            dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'isLoading', value: false, errorMessages: [] } });
        }
    };

    useEffect(() => {
        if (!state.data) return;
        reloadDashboard(timeRange);
    }, [timeRange]);

    const handleTimeRangeChange = (nextRange: string) => {
        if (nextRange === timeRange) return;
        dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'timeRange', value: nextRange, errorMessages: [] } });
    };

    const handleEditListing = async () => {
        if (!provider?.providerId) return;
        try {
            const detail = await ServiceProviderDashboardService.getProviderDetail(provider.providerId);
            setEditData(detail);
            setIsActionOpen(false);
        } catch (error) {
            console.error('Failed to load provider detail:', error);
            toast.error('Unable to load listing details');
        }
    };

    const handleDownloadReport = async () => {
        try {
            await ServiceProviderDashboardService.exportInquiryReportForTimeRange(timeRange);
            setIsActionOpen(false);
        } catch (error) {
            console.error('Download report failed:', error);
            toast.error('Failed to download report');
        }
    };

    const respondedWidth = `${Math.min(Math.max(funnel.respondedPct || 0, 0), 100)}%`;
    const convertedWidth = `${Math.min(Math.max(funnel.convertedPct || 0, 0), 100)}%`;
    const viewTrendClass = metrics.viewsTrendPct >= 0 ? 'text-success' : 'text-danger';
    const viewTrendIcon = metrics.viewsTrendPct >= 0 ? 'fa-arrow-up' : 'fa-arrow-down';
    const listingStatus = getListingStatusMeta(provider?.status, hasListing);
    const listingIsLive = listingStatus.code === 'ACTIVE';
    const bannerTimeRangeText = getTimeRangeDescription(timeRange);

    const handlePreviewListing = () => {
        if (provider?.userId) {
            window.open(`/alumni/${provider.userId}/profile`, '_blank');
        } else {
            toast.info('Preview is not available for this listing');
        }
        setIsActionOpen(false);
    };

    const handleUpdateListingStatus = async (nextStatus: string, confirmMessage: string, successMessage: string, failureMessage: string) => {
        if (!provider?.providerId) return;
        const confirmed = window.confirm(confirmMessage);
        if (!confirmed) return;

        try {
            await ServiceProviderDashboardService.updateProviderStatus(provider.providerId, nextStatus);
            toast.success(successMessage);
            setIsActionOpen(false);
            await reloadDashboard(timeRange);
        } catch (error) {
            console.error('Listing status update failed:', error);
            toast.error(failureMessage);
        }
    };

    const statusAction = (() => {
        switch ((provider?.status || '').toUpperCase()) {
            case 'ACTIVE':
                return {
                    key: 'pause',
                    label: 'Pause Listing',
                    icon: 'fas fa-pause-circle text-secondary',
                    onClick: () => handleUpdateListingStatus('INACTIVE', 'Pause this listing? It will stop appearing in active results.', 'Listing paused successfully', 'Failed to pause listing'),
                };
            case 'INACTIVE':
                return {
                    key: 'activate',
                    label: 'Activate Listing',
                    icon: 'fas fa-play-circle text-secondary',
                    onClick: () => handleUpdateListingStatus('ACTIVE', 'Activate this listing? It will become visible in active results.', 'Listing activated successfully', 'Failed to activate listing'),
                };
            case 'PAUSED':
            default:
                return {
                    key: 'resume',
                    label: 'Resume Listing',
                    icon: 'fas fa-play-circle text-secondary',
                    onClick: () => handleUpdateListingStatus('ACTIVE', 'Resume this listing? It will appear in active results again.', 'Listing resumed successfully', 'Failed to resume listing'),
                };
        }
    })();

    const manageActions = hasListing
        ? [
            statusAction,
            {
                key: 'download-report',
                label: 'Download Report',
                icon: 'fas fa-download text-secondary',
                onClick: handleDownloadReport,
            },
        ]
        : [];

    return (
        <SmartContext.Provider value={{ state, dispatch }}>
            <div className="main flex-1">
                {!state?.data && <LoadingSpinner />}
                {state?.data && (
                    <div className="bg-white py-5">
                        <div className="max-1140 mx-auto">
                            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
                                <div>
                                    <h1 className="fs-2 fw-semibold mb-0 d-flex flex-wrap align-items-center gap-2 text-darker-blue">
                                        {formConfig?.title || 'My Service Listing'}
                                        <span className={`d-inline-flex align-items-center gap-2 rounded-pill fw-medium px-3 py-2 ${listingStatus.badgeClass}`} style={{ fontSize: '13px' }}>
                                            <span className="live-dot" style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: listingStatus.dotColor, display: 'inline-block' }}></span>
                                            {listingStatus.label}
                                        </span>
                                    </h1>
                                    <p className="text-secondary mb-0">Track your listing performance and manage inquiries</p>
                                </div>
                                <div className="d-flex gap-2 flex-wrap">
                                    {hasListing ? (
                                        <>
                                            <button className="btn btn-outline-secondary d-flex align-items-center gap-2" onClick={handleEditListing}>
                                                <i className="fas fa-edit"></i> Edit Listing
                                            </button>
                                            <div className="dropdown position-relative">
                                                <button className="btn btn-outline-secondary d-flex align-items-center gap-2 dropdown-toggle" onClick={() => setIsActionOpen((current) => !current)}>
                                                    <i className="fas fa-cog"></i> Manage
                                                </button>
                                                {isActionOpen && (
                                                    <ul className="dropdown-menu dropdown-menu-end shadow border-0 rounded-3 mt-1 show" style={{ display: 'block' }}>
                                                        {manageActions.map((action, index) => (
                                                            <li key={action.key}>
                                                                {index > 0 && <hr className="dropdown-divider" />}
                                                                <button className="dropdown-item d-flex align-items-center gap-2 py-2" onClick={action.onClick}>
                                                                    <i className={action.icon}></i> {action.label}
                                                                </button>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        <AddServiceProviderModal className="btn btn-green px-4 text-decoration-none" modalTitle="Create Listing" />
                                    )}
                                </div>
                            </div>

                            <div className="bg-darker-blue rounded-3 p-4 mb-4 position-relative text-white">
                                <div className="position-relative" style={{ zIndex: 1 }}>
                                    <h2 className="font-22 fw-semibold mb-1">{hasListing ? `Welcome back, ${provider?.providerName || 'Service Provider'}!` : 'Create your service listing'}</h2>
                                    <p className="mb-1 opacity-75">{hasListing ? `Here's how your listing is performing ${bannerTimeRangeText}` : 'Publish your listing to start tracking views and managing inquiries from this dashboard.'}</p>
                                    <p className="mb-0 small opacity-50">{hasListing ? `Data as of ${formatDateTime(new Date())}` : 'Once your listing is live, performance data will appear here.'}</p>
                                </div>
                            </div>

                            <div className="row g-3 mb-4">
                                <div className="col-12 col-sm-6 col-xl-3">
                                    <div className="card metric-card border-0 shadow-sm h-100"><div className="card-body p-4">
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <span className="text-uppercase fw-medium small text-secondary" style={{ letterSpacing: '.5px' }}>Listing Views</span>
                                            <div className="d-flex align-items-center justify-content-center rounded-2 ratio-40 bg-primary bg-opacity-25"><i className="bi bi-eye-fill font-20 text-primary-subtle"></i></div>
                                        </div>
                                        <div className="fw-bold mb-1" style={{ fontSize: '32px', color: '#111827', lineHeight: 1.2 }}>{formatCount(metrics.listingViews)}</div>
                                        <div className={`d-flex align-items-center gap-1 small fw-medium ${viewTrendClass}`}><i className={`fas ${viewTrendIcon}`}></i> {Math.abs(metrics.viewsTrendPct)}% vs {metrics.comparisonLabel}</div>
                                        <div className="small text-secondary mt-1">All time: {formatCount(metrics.allTimeViews)} views</div>
                                    </div></div>
                                </div>
                                <div className="col-12 col-sm-6 col-xl-3">
                                    <div className="card metric-card border-0 shadow-sm h-100"><div className="card-body p-4">
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <span className="text-uppercase fw-medium small text-secondary" style={{ letterSpacing: '.5px' }}>Inquiries Received</span>
                                            <div className="d-flex align-items-center justify-content-center rounded-2 ratio-40 bg-primary bg-opacity-25"><i className="bi bi-envelope-fill font-20 text-primary-subtle"></i></div>
                                        </div>
                                        <div className="fw-bold mb-1" style={{ fontSize: '32px', color: '#111827', lineHeight: 1.2 }}>{formatCount(metrics.inquiriesReceived)}</div>
                                        <div className="d-flex align-items-center gap-1 small fw-medium text-success"><i className="fas fa-arrow-up"></i> {formatCount(metrics.newInquiries)} new in this period</div>
                                        <div className="small text-secondary mt-1">{formatCount(metrics.awaitingResponse)} awaiting response</div>
                                    </div></div>
                                </div>
                                <div className="col-12 col-sm-6 col-xl-3">
                                    <div className="card metric-card border-0 shadow-sm h-100"><div className="card-body p-4">
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <span className="text-uppercase fw-medium small text-secondary" style={{ letterSpacing: '.5px' }}>Responded</span>
                                            <div className="d-flex align-items-center justify-content-center rounded-2 ratio-40 bg-success bg-opacity-25"><i className="bi bi-reply-fill font-20 text-success"></i></div>
                                        </div>
                                        <div className="fw-bold mb-1" style={{ fontSize: '32px', color: '#111827', lineHeight: 1.2 }}>{formatCount(metrics.allTimeResponded)}</div>
                                        <span className="bg-success-subtle text-success rounded-2 font-14 fw-medium px-2 py-1">{metrics.responseRate}% response rate</span>
                                        <div className="small text-secondary mt-2">Avg. response time: {metrics.avgResponseDays} days</div>
                                    </div></div>
                                </div>
                                <div className="col-12 col-sm-6 col-xl-3">
                                    <div className="card metric-card border-0 shadow-sm h-100"><div className="card-body p-4">
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <span className="text-uppercase fw-medium small text-secondary" style={{ letterSpacing: '.5px' }}>Converted</span>
                                            <div className="d-flex align-items-center justify-content-center rounded-2 ratio-40 bg-warning bg-opacity-25"><i className="bi bi-arrow-left-right font-20 text-warning-subtle"></i></div>
                                        </div>
                                        <div className="fw-bold mb-1" style={{ fontSize: '32px', color: '#111827', lineHeight: 1.2 }}>{formatCount(metrics.allTimeConverted)}</div>
                                        <span className="bg-warning-subtle text-warning-emphasis rounded-2 font-14 fw-medium px-2 py-1">{metrics.conversionRate}% conversion rate</span>
                                        <div className="small text-secondary mt-2">Potential value: {formatCompactCurrency(metrics.potentialValue)}</div>
                                    </div></div>
                                </div>
                            </div>

                            <div className="row g-4 mb-4">
                                <div className="col-lg-8">
                                    <div className="bg-white border rounded-3 p-4 h-100">
                                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
                                            <div>
                                                <h4 className="font-22 font-600 mb-1">Listing Views Over Time</h4>
                                                <p className="font-14 text-muted mb-0">Track visibility trends for your listing.</p>
                                            </div>
                                            <div className="d-flex flex-wrap gap-2">
                                                {TIME_RANGE_OPTIONS.map((option) => (
                                                    <button
                                                        type="button"
                                                        key={option.code}
                                                        className={`btn px-3 height-35 ${timeRange === option.code ? 'btn-darkblue text-white' : 'btn-border-gray'}`}
                                                        onClick={() => handleTimeRangeChange(option.code)}>
                                                        <span>{option.label}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div style={{ height: '320px' }}>
                                            <Line data={chartConfig.data as any} options={chartConfig.options as any} />
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-4">
                                    <div className="card border-0 shadow-sm h-100 new-section">
                                        <div className="card-body p-4">
                                            <h3 className="mb-4 fw-medium font-18 text-secondary-emphasis d-flex align-items-center gap-2">
                                                Inquiry Funnel <i className="fas fa-info-circle small text-secondary"></i>
                                            </h3>
                                            <div className="d-flex flex-column gap-3">
                                                <div>
                                                    <div className="bg-primary text-white rounded-2 py-2 px-3 d-flex justify-content-between align-items-center w-100">
                                                        <span className="fw-medium small">Total Inquiries</span>
                                                        <span className="fw-semibold">{formatCount(funnel.total)}</span>
                                                    </div>
                                                </div>
                                                <div className="d-flex align-items-center gap-2 bg-gray-f5 rounded-2 overflow-hidden">
                                                    <div className="flex-grow-1">
                                                        <div className="bg-success text-white rounded-2 py-2 px-3 d-flex justify-content-between align-items-center" style={{ width: respondedWidth }}>
                                                            <span className="fw-medium small">Responded</span>
                                                            <span className="fw-semibold">{formatCount(funnel.responded)}</span>
                                                        </div>
                                                    </div>
                                                    <span className="small text-secondary-emphasis fw-medium pe-2">{funnel.respondedPct || 0}%</span>
                                                </div>
                                                <div className="d-flex align-items-center gap-2 bg-gray-f5 rounded-2 overflow-hidden">
                                                    <div className="flex-grow-1">
                                                        <div className="bg-warning text-dark rounded-2 py-2 px-3 d-flex justify-content-between align-items-center" style={{ width: convertedWidth }}>
                                                            <span className="fw-medium small">Converted</span>
                                                            <span className="fw-semibold">{formatCount(funnel.converted)}</span>
                                                        </div>
                                                    </div>
                                                    <span className="small text-secondary-emphasis fw-medium pe-2">{funnel.convertedPct || 0}%</span>
                                                </div>
                                            </div>
                                            <p className="text-center small text-secondary mt-3 mb-0">
                                                <i className="fas fa-lightbulb text-warning me-1"></i>Tip: Responding within 24 hours increases conversion by 35%
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="row g-4">
                                <div className="col-lg-8">
                                    <div className="bg-white border rounded-3 p-4 h-100">
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h4 className="font-22 font-600 mb-0">Recent Inquiries</h4>
                                            <span className="font-14 text-muted">Last 5</span>
                                            {/*
                                            add link to the full inquiry management page if needed, e.g. <Link to="/alumni/inquiries" className="font-14 text-secondary">View All</Link>
                                            */}
                                            <Link to="/alumni/inquiry-management" className="btn btn-skyblue btn-sm px-3">View All</Link>
                                        </div>
                                        {recentInquiries.length === 0 ? (
                                            <div className="text-center py-5">
                                                <h5 className="font-16 font-600">No inquiries found</h5>
                                                <p className="text-muted mb-0">New inquiries will appear here.</p>
                                            </div>
                                        ) : (
                                            <div className="table-responsive attendee_dtl bg-white rounded-3">
                                                <table className="table mb-0" style={{ minWidth: '820px' }}>
                                                    <thead>
                                                        <tr>
                                                            <th>Inquirer</th>
                                                            <th>Subject</th>
                                                            <th>Received</th>
                                                            <th>Status</th>
                                                            <th>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {recentInquiries.map((row: any) => (
                                                            <tr key={row.id}>
                                                                <td>
                                                                    <div className="font-16 font-500">{row.inquirerName || '-'}</div>
                                                                    <div className="font-13 text-muted">{row.inquirerEmail || '-'}</div>
                                                                </td>
                                                                <td>{row.subject || '-'}</td>
                                                                <td>{formatRelativeDate(row.createdAt)}</td>
                                                                <td>
                                                                    <span className={`badge rounded-pill px-3 py-2 font-13 ${row.status === 'RESPONDED' ? 'bg-success-subtle text-success' : row.status === 'VIEWED' ? 'bg-info-subtle text-info' : 'bg-primary-subtle text-primary'}`}>
                                                                        {row.status || 'NEW'}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <button type="button" className="btn btn-skyblue btn-sm px-3" onClick={() => setSelectedInquiry(row)}>
                                                                        View
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="col-lg-4">
                                    <div className="bg-white border rounded-3 p-4 h-100">
                                        <h4 className="font-22 font-600 mb-3">Quick Actions</h4>
                                        <div className="d-grid gap-2">
                                            {hasListing ? (
                                                <button type="button" className="btn btn-green height-40" onClick={handleEditListing}><span>Edit Listing</span></button>
                                            ) : (
                                                <AddServiceProviderModal className="btn btn-green height-40 text-decoration-none text-center" modalTitle="Create Listing" />
                                            )}
                                            {provider?.providerId ? (
                                                <AlumniDetailsModal className="btn btn-border-gray height-40 text-decoration-none text-center" modalTitle="View Public Listing" providerId={String(provider.providerId)} />
                                            ) : (
                                                <button type="button" className="btn btn-border-gray height-40" disabled><span>View Public Listing</span></button>
                                            )}
                                            <button type="button" className="btn btn-border-gray height-40" onClick={handleDownloadReport} disabled={!hasListing}><span>Download Report</span></button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {!hasListing && (
                                <div className="text-center mt-4">
                                    <AddServiceProviderModal className="btn btn-green px-4 text-decoration-none" modalTitle="Create Listing" />
                                </div>
                            )}

                            <InquiryDetailModal inquiry={selectedInquiry} show={!!selectedInquiry} onSaved={() => reloadDashboard(timeRange)} onHide={() => setSelectedInquiry(null)} />

                            {editData && (
                                <AddServiceProviderModal
                                    modalTitle="Edit Listing"
                                    className="d-none"
                                    editData={editData}
                                    onEditClose={() => {
                                        setEditData(null);
                                        reloadDashboard(timeRange);
                                    }}
                                />
                            )}
                        </div>
                    </div>
                )}
                <DebugBarControl />
            </div>
        </SmartContext.Provider>
    );
};

export default ServiceProviderDashboard;